import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showError, showSuccess } from "../../helpers/toast";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

API.interceptors.response.use(
  (response) => Promise.resolve(response),
  (error) => {
    // Display error message
    console.error(error.response?.data?.message || error.message);
    return Promise.reject(error);
  }
);

// Async thunk for creating a notification
export const createNotification = createAsyncThunk(
  "notifications/createNotification",
  async ({ message, user_id, accessToken, is_admin, subject, htmlMailContent, sendToGamingBoardAdmins, sendToGamingBoardReviewers, sendToGamingBoardViewers, sendToCompanyAdmins, applicationId, applicationName, status, companyName, employeeName }, { rejectWithValue }) => {
    console.log("createNotification thunk called with args:", { status, companyName, employeeName, applicationId, user_id });
    try {

      // Generate generic message if not provided
      let finalMessage = message;
      let finalHtmlMailContent = htmlMailContent;
      let finalSubject = subject;

      let fetchedCompanyName = companyName;
      let fetchedEmployeeName = employeeName;
      try {
        const userConfig = {
          headers: {
            "ngrok-skip-browser-warning": "69420",
            Authorization: `${accessToken}`,
            "Content-Type": "application/json",
          },
        };

        // Try to fetch true employee directly from the Application using applicationId
        let employeeUserId = null;
        let appData = null;
        if (applicationId) {
          try {
            const appResponse = await API.get(`/applications/${applicationId}`, userConfig);
            if (appResponse.data) {
              appData = appResponse.data.application || appResponse.data;
              employeeUserId = appData.user_id || appData.userId || appData.user?.id;
              
              // If we don't have a company name yet, check the application record itself
              if (!fetchedCompanyName || fetchedCompanyName.includes('@')) {
                const appCompanyName = appData.company_name || appData.companyName || appData.company?.name || appData.company?.company_name;
                if (appCompanyName && !appCompanyName.includes('@')) {
                  fetchedCompanyName = appCompanyName.trim();
                }
              }
            }
          } catch (appErr) {
            console.error("Failed to fetch application to find employee", appErr);
          }
        }

        // Use the application's true user_id if we found it; otherwise fallback to the notification's user_id
        const targetUserId = employeeUserId || user_id;

        if (targetUserId) {
          const userResponse = await API.get(`/users/${targetUserId}`, userConfig);
          console.log("notificationSlice DEBUG - userResponse.data:", userResponse.data);
          if (userResponse.data) {
            const userData = userResponse.data.user || userResponse.data;
            const isCompanyAdmin = userData.user_role === 'company_admin' || userData.role === 'company_admin';

            // 1. Initial Name Identification
            if (isCompanyAdmin) {
              // For company admin, prioritize company-specific fields if they exist
              if (!fetchedCompanyName || fetchedCompanyName.includes('@')) {
                const priorityName = (userData.company?.company_name || userData.company_name || userData.companyName || userData.company?.full_name || userData.company?.name)?.trim();
                if (priorityName && !priorityName.includes('@')) {
                  fetchedCompanyName = priorityName;
                }
              }
              // Fallback to full_name if still missing or email
              if (!fetchedCompanyName || fetchedCompanyName.includes('@')) {
                const fallbackName = (userData.full_name || userData.fullName || userData.name)?.trim();
                if (fallbackName) {
                  fetchedCompanyName = fallbackName;
                }
              }
              // For employee name, use the admin's name
              if (!fetchedEmployeeName) {
                fetchedEmployeeName = (userData.full_name || userData.fullName || userData.name)?.trim();
              }
            } else {
              // It's likely an employee
              if (!fetchedEmployeeName || fetchedEmployeeName === 'Employee' || fetchedEmployeeName === applicationName) {
                fetchedEmployeeName = (userData.full_name || userData.fullName || userData.name)?.trim();
              }
            }

            // 2. Fetch via company_id from /company/ endpoint (Dedicated profile)
            // If name is missing or still looks like an email/placeholder
            const nameIsPlaceholder = !fetchedCompanyName || 
                                     fetchedCompanyName.includes('@') || 
                                     fetchedCompanyName.includes('.com') || 
                                     fetchedCompanyName.toLowerCase() === 'employee' ||
                                     fetchedCompanyName.toLowerCase() === 'your company';

            if (nameIsPlaceholder && (userData.company_id || userData.company?.id)) {
              const cid = userData.company_id || userData.company?.id;
              try {
                // TRY THE DEDICATED /company/ endpoint instead of /users/
                const companyResponse = await API.get(`/company/${cid}`, userConfig);
                console.log("notificationSlice DEBUG - companyResponse.data (via /company/):", companyResponse.data);
                const companyProfile = companyResponse.data.company || companyResponse.data;
                if (companyProfile) {
                  const companyProfileName = (companyProfile.company_name || companyProfile.companyName || companyProfile.full_name || companyProfile.fullName || companyProfile.name)?.trim();
                  
                  if (companyProfileName && !companyProfileName.includes('@')) {
                    fetchedCompanyName = companyProfileName;
                  }
                }
              } catch (companyErr) {
                console.error("Failed to fetch dedicated company profile via /company/", companyErr);
                // Fallback attempt via /users/ if /company/ failed
                try {
                  const userCompResponse = await API.get(`/users/${cid}`, userConfig);
                  const userCompData = userCompResponse.data.user || userCompResponse.data;
                  const userCompName = (userCompData.company_name || userCompData.companyName || userCompData.full_name || userCompData.fullName)?.trim();
                  if (userCompName && !userCompName.includes('@')) {
                    fetchedCompanyName = userCompName;
                  }
                } catch (uErr) {}
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch user/company details", err);
      }

      if (status) {
        const appNo = applicationId ? `GB-${applicationId}` : "";
        const appName = applicationName || 'Application';
        const coName = fetchedCompanyName || 'your company';
        const empName = fetchedEmployeeName || 'Employee';
        console.log("notificationSlice DEBUG - Template Variables:", { appNo, appName, coName, empName, status });

        const statusMap = {
          // Status to Display Name (for emails/simple updates)
          "descrepency_GB_GB": "Under Review by Gaming Board",
          "descrepency_GB_Company": "Returned to Company (Discrepancy GB)",
          "descrepency_GB_Employee": "Returned to Employee (Discrepancy GB)",
          "dicrepancy_application_assigned_to_comapany_user": "Assigned to employee",
          "application_sent_to_discrepancy_by_company": "Assigned to Company",
          "in_process": "In Process",
          "approved": "Approved",
          "rejected": "Rejected",
          "drafts": "Draft",
          "submitted": "In Review",
          "certificate_issued": "Certificate Issued",
          "employee_added": "Employee Added",
          "employee_deleted": "Employee Deleted",
          "employee_updated": "Employee Updated",
          "company_submitted": "Submitted to Gaming Board"
        };

        const statusDescription = statusMap[status] || status;

        // Custom messages based on status/event (Matching the spreadsheet)
        if (!finalMessage) {
          if (status === "in_process") {
            if (empName && coName && empName !== 'Employee') {
              // Row 3: forwarded by Co for Emp
              finalMessage = `Application #${appNo} – ${appName} submitted by ${coName} for ${empName} has been Submitted to the Gaming Board for review.`;
            } else {
              // Row 1: Company-led submission
              finalMessage = `Application #${appNo} – ${appName} has been successfully submitted by ${coName}, available for review.`;
            }
          } else if (status === "company_submitted") {
            // Row 1: Company Admin submitted to GB
            finalMessage = `Application #${appNo} – ${appName} has been successfully submitted by ${coName}.`;
          } else if (status === "submitted") {
            // Row 2: Employee submitted to Company
            finalMessage = `Employee ${empName} has submitted application #${appNo} – ${appName}, available for review.`;
          } else if (status === "approved") {
            // Row 4
            finalMessage = `Application #${appNo} – ${appName} has been approved by the Gaming Board.`;
          } else if (status === "rejected") {
            // Row 5
            finalMessage = `Application #${appNo} – ${appName} has been rejected by the Gaming Board.`;
          } else if (status === "certificate_issued") {
            // Row 6
            finalMessage = `The certificate for application #${appNo} – ${appName} has been issued by the Gaming Board.`;
          } else if (status === "discrepancies" || status === "descrepency_GB_Company") {
            // Row 7 & 8
            finalMessage = `A discrepancy has been raised for application #${appNo} – ${appName} by the Gaming Board.`;
          } else if (status === "descrepency_GB_GB") {
            // Row 9: Resolved and resubmitted to GB
            finalMessage = `Discrepancies for application #${appNo} – ${appName} have been resolved and resubmitted by ${coName}.`;
          } else if (status === "descrepency_GB_Employee") {
            // Row 10: Co -> Emp (GB initiated discrepancy)
            finalMessage = `A discrepancy has been raised for application #${appNo} – ${appName} assigned to you by ${coName}.`;
          } else if (status === "application_sent_to_discrepancy_by_company") {
            // Row 14: Co -> Emp (Co initiated discrepancy)
            finalMessage = `A discrepancy has been corrected by the ${empName} and Assigned to ${coName} for application ${appNo} , ${appName}`;
          } else if (status === "dicrepancy_application_assigned_to_comapany_user") {
            // Row 15: Assigning within company
            finalMessage = `A discrepancy has been Assigned by ${coName} for your application #${appNo} – ${appName}.`;
          } else if (status === "employee_added") {
            // Row 18
            finalMessage = `Employee ${empName} added to company ${coName}.`;
          } else if (status === "employee_deleted") {
            // Row 19
            finalMessage = `Employee ${empName} deleted from company ${coName}.`;
          } else if (status === "employee_updated") {
            // Row 20
            finalMessage = `${empName} information updated from ${coName}.`;
          } else {
            // Fallback
            finalMessage = applicationId
              ? `Application ${appName} (ID: ${appNo}) status updated to: ${statusDescription}`
              : `Status updated to: ${statusDescription}`;
          }
        }

        if (!finalSubject) {
          finalSubject = applicationId
            ? `Application Status Update: ${appNo}`
            : `Notification: ${statusDescription}`;
        }

        if (!finalHtmlMailContent) {
          finalHtmlMailContent = `
            <div style="font-family: Arial, sans-serif; padding: 20px; color: #333;">
              <h2 style="color: #2c3e50;">Application Update</h2>
              <p>Dear User,</p>
              <p>${finalMessage}</p>
              <p><strong>Current Status:</strong> <span style="color: #007bff;">${statusDescription}</span></p>
              <p>Please log in to your dashboard to view more details.</p>
              <br>
              <p>Best regards,</p>
              <p>Gaming Board Team</p>
            </div>`;
        }
      }

      // Enforce Gaming Board routing for company submissions
      let finalSendToGamingBoardAdmins = sendToGamingBoardAdmins;
      let finalSendToGamingBoardReviewers = sendToGamingBoardReviewers;
      let finalSendToGamingBoardViewers = sendToGamingBoardViewers;

      if (status === "company_submitted") {
        finalSendToGamingBoardAdmins = true;
        finalSendToGamingBoardReviewers = true;
        finalSendToGamingBoardViewers = true;
      }

      console.log(finalMessage, user_id);
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };

      // Use defaults if flags are not explicitly provided
      const finalSendToCompanyAdmins = sendToCompanyAdmins !== undefined ? sendToCompanyAdmins : true;
      // For GB admins, usually only send if specifically requested or status implies it? 
      // User said "Make is consistant for all", implying we should be generous with notifications or stick to specific flags?
      // For now, respect passed flags, defaulting detailed flags to false if undefined to avoid spam, 
      // unless we want to default to true? The prompt said "make it consistant".
      // Existing code passed explicit true/false. I will stick to passed values, but maybe default to false to be safe unless logic dictates.
      // actually, let's keep the user's explicit flags from the call site if present.

      const response = await API.post(
        "/notification/",
        // Map user_id (thunk arg) to userId (API expected key)
        { message: finalMessage, userId: user_id, is_admin, sendToGamingBoardAdmins: finalSendToGamingBoardAdmins, sendToGamingBoardReviewers: finalSendToGamingBoardReviewers, sendToGamingBoardViewers: finalSendToGamingBoardViewers, sendToCompanyAdmins: finalSendToCompanyAdmins },
        config
      );
      try {
        await API.post(
          "/users/sendMail",
          {
            userId: user_id,
            subject: finalSubject || "Notification",
            htmlMailContent: finalHtmlMailContent || "<p>check your account your application status is updated</p>",
            sendToGamingBoardAdmins: finalSendToGamingBoardAdmins || false,
            sendToGamingBoardViewers: finalSendToGamingBoardViewers || false,
            sendToGamingBoardReviewers: finalSendToGamingBoardReviewers || false,
            sendToCompanyAdmins: finalSendToCompanyAdmins
          },
          config
        );
      } catch (emailError) {
        console.error("Failed to send email notification", emailError);
      }
      // showSuccess('Notification created successfully');
      return response.data;
    } catch (error) {
      showError(error.response?.data?.error || "Error");
      return rejectWithValue(
        error.response?.data?.message || "Failed to create notification"
      );
    }
  }
);

// Async thunk for updating a notification
export const updateNotification = createAsyncThunk(
  "notifications/updateNotification",
  async (
    { notificationId, message, read_status, accessToken },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.put(
        `/notification/${notificationId}`,
        { message, read_status },
        config
      );
      //   showSuccess('Notification updated successfully');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update notification"
      );
    }
  }
);

// Async thunk for deleting a notification
export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async ({ configdata }, { rejectWithValue }) => {
    console.log(configdata.notification_ids, "nnnnnnnnnnnnnnnnn");
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${configdata.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.delete(
        `/notification/${configdata.notification_ids}`,
        config
      );
      //   showSuccess('Notification deleted successfully');
      return configdata.notification_ids;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete notification"
      );
    }
  }
);

// Async thunk for fetching notifications
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async ({ accessToken, queryParams }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
        params: queryParams,
      };
      const response = await API.get("/notification/", config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

const initialState = {
  notifications: [],
  pagination: null,
  unreadCount: 0,
  loading: false,
  error: null,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: {
    [createNotification.pending]: (state) => {
      state.loading = true;
    },
    [createNotification.fulfilled]: (state, action) => {
      state.loading = false;
      state.notifications.push(action.payload);
      state.error = null;
    },
    [createNotification.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    [updateNotification.fulfilled]: (state, action) => {
      state.loading = false;
      const index = state.notifications.findIndex(
        (notification) => notification.id === action.payload.id
      );
      if (index !== -1) {
        state.notifications[index] = { ...state.notifications[index], ...action.payload };
      }
      state.error = null;
    },
    [deleteNotification.fulfilled]: (state, action) => {
      state.loading = false;
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
      state.error = null;
    },
    [fetchNotifications.pending]: (state) => {
      state.loading = true;
    },
    [fetchNotifications.fulfilled]: (state, action) => {
      state.loading = false;
      // If isCountOnly is true, ONLY update unreadCount
      // This prevents background fetches (like from Header) from overwriting
      // the full list that the Notifications page might be displaying
      if (action.meta.arg.isCountOnly) {
        state.unreadCount = action.payload.unreadCount || 0;
      } else {
        state.notifications = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || null;
        state.unreadCount = action.payload.unreadCount || 0;
      }
      state.error = null;
    },
    [fetchNotifications.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export default notificationSlice.reducer;
